import { Button, Menu, MultiSelect, Paper, ScrollArea, SegmentedControl, Stack, Textarea, Text, Group, Radio, TextInput, MultiSelectProps, Tooltip } from "@mantine/core"
import { useForm } from "@mantine/form"
import { IconChevronDown, IconSend } from "@tabler/icons-react"
import { RichTextEditor, Link } from '@mantine/tiptap';
import { useEditor } from "@tiptap/react";
import { useEffect, useState } from "react";
import { modals } from "@mantine/modals";
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Color } from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import SubScript from '@tiptap/extension-subscript';
import { useSendTeamsMessageProxy, useStaffOidsSuspense } from "../query";
import { useResolveStudents } from "@features/communications/queries";
import { RecipientResolutionModal, type RecipientResolutionModalView } from "@features/communications/components/RecipientResolutionModal";
import classes from './SendTeamsMessageForm.module.css';
import { TeamsMessage } from "@godalming/api-types";
import { StudentRecipientSelect } from "@features/student-lists/components/StudentRecipientSelect";

export type TeamsStudent = {
    studentNumber: string;
    firstName: string;
    surname: string;
    uuid: string | null;
}

interface SendTeamsMessageFormProps {
    defaultSelectedStudents?: string[];
    defaultSelectedStaff?: string[];
    suggestedStudents?: string[];
    defaultType?: 'oneOnOne' | 'group';
    allowSelectionChange?: boolean;
    suggestedRecipients?: string;
    onSuccess?: () => void;
}

const toStudentSelector = (value: string) =>
    value.toLowerCase().startsWith('student:') ? value : `student:${value}`;

export const SendTeamsMessageForm: React.FC<SendTeamsMessageFormProps> = ({ defaultSelectedStudents = [], defaultSelectedStaff = [], suggestedStudents, defaultType = 'oneOnOne', allowSelectionChange = true, onSuccess }) => {

    const form = useForm<TeamsMessage>({
        initialValues: {
            type: defaultType,
            studentRecipients: defaultSelectedStudents.map(toStudentSelector),
            staffRecipients: [...defaultSelectedStaff],
            message: '',
            sendAs: 'self',
        },
    })

    const renderTagsInputOption: MultiSelectProps['renderOption'] = ({ option }) => (
        <Text size="sm">{option.label}</Text>
    );

    const editor = useEditor({
        shouldRerenderOnTransaction: true,
        extensions: [
            StarterKit, 
            Link, 
            Placeholder.configure({
                placeholder: 'Enter your message',
            }),
            Color,
            TextAlign,
            Superscript,
            SubScript,
        ],
        content: '',
    });

    const [textMode, setTextMode] = useState<'plain' | 'rich'>('rich');
    const [recipientError, setRecipientError] = useState<string | null>(null);
    const [messageError, setMessageError] = useState<string | null>(null);
    const [resolvedStudentRecipients, setResolvedStudentRecipients] = useState<string[]>(defaultSelectedStudents);
    const [resolutionModalView, setResolutionModalView] = useState<RecipientResolutionModalView | null>(null);
    const studentResolutionQuery = useResolveStudents({ selectors: form.values.studentRecipients });

    const sendMessageMutation = useSendTeamsMessageProxy()

    useEffect(() => {
        if (form.values.studentRecipients.length + form.values.staffRecipients.length > 0) {
            setRecipientError(null);
        }
    }, [form.values.staffRecipients.length, form.values.studentRecipients.length])

    function hasMessageContent(message: string) {
        return message
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .trim()
            .length > 0;
    }

    function sendMessage(params: TeamsMessage) {
        sendMessageMutation.mutate(params, {
            onSuccess: () => {
                form.reset();
                editor?.commands.setContent('');
                onSuccess?.();
            },
        });
    }

    async function handleSubmit(values: typeof form.values) {

        const message = textMode === 'plain' ? values.message : editor?.getHTML() ?? '';
        const recipientCount = resolvedStudentRecipients.length + values.staffRecipients.length;
        const nextRecipientError = recipientCount === 0 ? 'Select at least one recipient.' : null;
        const nextMessageError = hasMessageContent(message) ? null : 'Enter a message.';

        setRecipientError(nextRecipientError);
        setMessageError(nextMessageError);

        if (nextRecipientError || nextMessageError) {
            return;
        }

        const params: TeamsMessage = {
            ...values,
            studentRecipients: [...resolvedStudentRecipients],
            staffRecipients: [...values.staffRecipients],
            message,
        };

        const isGroupChat = params.type === 'group';

        modals.openConfirmModal({
            title: isGroupChat ? 'Create Teams group chat' : 'Send Teams message',
            children: (
                <Text size="sm">
                    {isGroupChat
                        ? `Create a Teams group chat with ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}?`
                        : `Send Teams message to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}?`}
                </Text>
            ),
            labels: {
                confirm: isGroupChat ? 'Create group chat' : 'Send message',
                cancel: 'Cancel',
            },
            confirmProps: {
                leftSection: <IconSend size={16} />,
                loading: sendMessageMutation.isPending,
            },
            onConfirm: () => sendMessage(params),
        });
    }

    const data = [
        {   
            name: 'One on one chats', 
            description: 'Send a one on one chat to each recipient.',
            value: 'oneOnOne',
        },
        {
            name: 'Group chat',
            description: 'Create a group chat with the selected recipients.',
            value: 'group',
        }
    ];

    const { data: allStaffOids } = useStaffOidsSuspense();

    const staffOptions = Array.from(
        new Map(allStaffOids.map(oid => [
            oid.oid,
            {
                value: oid.oid,
                label: `${oid.preferredName || oid.firstName} ${oid.surname} (${oid.initials ? oid.initials.toLowerCase() : 'No initials'})`,
            },
        ])).values(),
    );

    const visibleSuggestedStudents = (suggestedStudents ?? [])
        .filter((studentNo) => !form.values.studentRecipients.includes(studentNo))
        .map((studentNo) => (
            { value: studentNo, label: studentNo }
        ));

    const addSuggestedStudent = (studentNo: string) => {
        form.setFieldValue('studentRecipients', [
            ...form.values.studentRecipients,
            toStudentSelector(studentNo),
        ]);
        setRecipientError(null);
    };

    return (
        <form 
            id="send-teams-message-form"
            onSubmit={form.onSubmit(handleSubmit)}
        >
            <Stack>
                <Radio.Group
                    {...form.getInputProps('type')}
                >
                    <Group gap="xs" wrap="nowrap" align="stretch" justify="stretch">
                        {data.map((item) => (
                        <Radio.Card className={classes.root} value={item.value} key={item.value} p={'md'} py={'xs'}>
                            <Group wrap="nowrap" align="flex-start">
                                <Radio.Indicator size="sm"/>
                                <div>
                                    <Text size="sm" fw={500}>{item.name}</Text>
                                    <Text size="xs" c={'dimmed'}>{item.description}</Text>
                                </div>
                            </Group>
                        </Radio.Card>
                        ))}
                    </Group>
                </Radio.Group>
                {/* <Select 
                    label="Send as"
                    description="Choose who the message will appear from"
                    data={[
                        {
                            value: 'self',
                            label: 'Me',
                        },
                        {
                            value: 'college',
                            label: 'Godalming College'
                        }
                    ]}
                    className={'w-fit'}
                    {...form.getInputProps('sendAs')}
                /> */}
                <Stack gap={3}>
                    <Stack gap={5}>
                    <Group gap="xs" align="flex-end" wrap="nowrap">
                        <StudentRecipientSelect
                            label={`Student Recipients (${form.values.studentRecipients.length})`}
                            renderOption={renderTagsInputOption}
                            value={form.values.studentRecipients}
                            onChange={(value) => form.setFieldValue('studentRecipients', value)}
                            onResolvedChange={setResolvedStudentRecipients}
                            studentValuePrefix="student:"
                            withCheckIcon={true}
                            hidePickedOptions={true}
                            styles={{
                                input: {
                                    maxHeight: '8rem',
                                    overflow: 'auto'
                                }
                            }}
                            searchable
                            nothingFoundMessage="No students found"
                            withPillsReorder
                            clearSectionMode='both'
                            limit={50}
                            flex={1}
                        />
                        <Button
                            type="button"
                            variant="light"
                            onClick={() => setResolutionModalView({ type: 'students' })}
                        >
                            View students
                        </Button>
                    </Group>
                        {visibleSuggestedStudents.length > 0 && (
                            <Menu shadow="md" width={260} position="bottom-end" closeOnItemClick={false}>
                                <Menu.Target>
                                    <Button
                                        type="button"
                                        size="compact-xs"
                                        variant="light"
                                        rightSection={<IconChevronDown size={14} />}
                                        className="w-fit! self-end"
                                    >
                                        Suggested
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <ScrollArea.Autosize mah={260} type="auto">
                                        {visibleSuggestedStudents.map((student) => (
                                            <Menu.Item
                                                key={student.value}
                                                onClick={() => addSuggestedStudent(student.value)}
                                            >
                                                {student.label}
                                            </Menu.Item>
                                        ))}
                                    </ScrollArea.Autosize>
                                </Menu.Dropdown>
                            </Menu>
                        )}
                    </Stack>
                    <MultiSelect 
                        label={`Staff Recipients (${form.values.staffRecipients.length})`}
                        renderOption={renderTagsInputOption}
                        data={staffOptions}
                        withCheckIcon={true}
                        hidePickedOptions={true}
                        styles={{
                            input: {
                                maxHeight: '8rem',
                                overflow: 'auto'
                            }
                        }}
                        searchable
                        nothingFoundMessage="No staff found"
                        limit={50}
                        withPillsReorder
                        clearSectionMode='both'
                        {...form.getInputProps('staffRecipients')}
                    />
                    {recipientError && (
                        <Text size="xs" c="red">
                            {recipientError}
                        </Text>
                    )}
                </Stack>
                {form.values.type === 'group' && (
                    <TextInput
                        label="Topic"
                        placeholder="Enter the topic"
                        {...form.getInputProps('topic')}
                    />
                )}
                <Stack gap={3}>
                    <Text fw={500} size="sm">Message</Text>
                    <Stack gap={5}>
                        <Paper p={0} withBorder>
                            <SegmentedControl
                                value={textMode}
                                onChange={(value) => setTextMode(value as 'plain' | 'rich')}
                                data={[
                                    { label: 'Plain Text', value: 'plain' },
                                    { label: 'Rich Text', value: 'rich' },
                                ]}
                                size="xs"
                                className="w-full!"
                            />
                        </Paper>
                        {textMode === 'plain' && 
                        <Textarea
                            placeholder="Enter your message"
                            autosize
                            minRows={3}
                            {...form.getInputProps('message')}
                            error={messageError}
                        />}
                        {textMode === 'rich' && 
                        <RichTextEditor 
                            editor={editor} 
                            //variant='subtle' 
                            styles={{
                                Typography: {
                                    fontSize: 'var(--mantine-font-size-sm)',
                                },
                            }}
                        >
                            <RichTextEditor.Toolbar sticky stickyOffset={60} p={3}>
                                <RichTextEditor.ControlsGroup>
                                    <RichTextEditor.Bold />
                                    <RichTextEditor.Italic />
                                    <RichTextEditor.Underline />
                                    <RichTextEditor.ColorPicker
                                        colors={[
                                            '#25262b',
                                            '#868e96',
                                            '#fa5252',
                                            '#e64980',
                                            '#be4bdb',
                                            '#7950f2',
                                            '#4c6ef5',
                                            '#228be6',
                                            '#15aabf',
                                            '#12b886',
                                            '#40c057',
                                            '#82c91e',
                                            '#fab005',
                                            '#fd7e14',
                                        ]}
                                    />
                                </RichTextEditor.ControlsGroup>

                                <RichTextEditor.ControlsGroup>
                                    <RichTextEditor.BulletList />
                                    <RichTextEditor.OrderedList />
                                    <RichTextEditor.Subscript />
                                    <RichTextEditor.Superscript />
                                </RichTextEditor.ControlsGroup>

                                <RichTextEditor.ControlsGroup>
                                    <RichTextEditor.Link />
                                    <RichTextEditor.Unlink />
                                </RichTextEditor.ControlsGroup>
                            </RichTextEditor.Toolbar>
                            <RichTextEditor.Content className={classes.editorContent} p={0}/>
                        </RichTextEditor>}
                        {textMode === 'rich' && messageError && (
                            <Text size="xs" c="red">
                                {messageError}
                            </Text>
                        )}
                    </Stack>
                </Stack>
                <Button 
                    type="submit" 
                    leftSection={<IconSend size={16} />} 
                    mt="md"
                    loading={sendMessageMutation.isPending}
                    disabled={false}
                >
                    {form.values.type === 'group' ? 'Create group chat' : 'Send'}
                </Button>
            </Stack>
            <RecipientResolutionModal
                view={resolutionModalView}
                onClose={() => setResolutionModalView(null)}
                students={studentResolutionQuery.data?.students ?? []}
                recipients={{ to: [], cc: [], bcc: [] }}
                loading={studentResolutionQuery.isFetching}
            />
        </form>
    )
}
